import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import moment from 'moment';
import { split, round, flatMap } from 'lodash';
import axios from '../../config/axios';

const KW_GREEN = '#046333';
const KW_GREEN_LIGHT = '#E3F0E9';

// ─── Helpers ────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ minHeight: 300 }}>
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

const getItemStatus = (item) => {
  let subItems = item.sub_items;
  if (typeof subItems === 'string') {
    try { subItems = JSON.parse(subItems) || []; } catch { subItems = []; }
  }
  subItems = Array.isArray(subItems) ? subItems : [];
  const hasReturnBarcode = subItems.some(si => si?.return_barcode_image_url);
  if (item.return_id && hasReturnBarcode) return 'Devolvido';
  if (item.return_id) return 'Devolução agendada';
  const hasBarcode = subItems.some(si => si?.barcode_id || si?.barcode_image_url);
  if (!hasBarcode) return 'Em processamento';
  return 'Em armazém';
};

const itemStatusStyle = (s) => {
  if (s === 'Em armazém') return { background: KW_GREEN_LIGHT, color: KW_GREEN };
  if (s === 'Em processamento') return { background: '#fef3c7', color: '#92400e' };
  if (s === 'Devolução agendada') return { background: '#dbeafe', color: '#1e40af' };
  return { background: '#f3f4f6', color: '#6b7280' };
};

const orderStatusLabel = (s) => ({
  processing: 'Em processamento',
  confirmed: 'Confirmado',
  completed: 'Concluído',
  cancelled: 'Cancelado',
}[s] || s || '—');

const freqLabel = (f) => ({
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  semester: 'Semestral',
  annual: 'Anual',
}[f] || f || '—');

const invoiceTypeLabel = (type) => ({
  subscription: 'Subscrição',
  pickup: 'Recolha',
  return: 'Devolução',
  partial_return: 'Dev. Parcial',
  extras: 'Extras',
  shipping: 'Transporte',
}[type] || type || '—');

// ─── Sub-components ──────────────────────────────────────────────────────────

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 24 }}>
    <Typography
      variant="h6"
      style={{ color: KW_GREEN, fontWeight: 600, marginBottom: 12, fontSize: '1rem', borderBottom: `2px solid ${KW_GREEN_LIGHT}`, paddingBottom: 6 }}
    >
      {title}
    </Typography>
    {children}
  </div>
);

const InfoGrid = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px 24px', marginBottom: 8 }}>
    {children}
  </div>
);

const InfoItem = ({ label, value, mono }) => (
  <div>
    <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
      {label}
    </div>
    <div style={{ fontSize: 14, fontFamily: mono ? 'monospace' : 'inherit', fontWeight: 500, wordBreak: 'break-all' }}>
      {value || '—'}
    </div>
  </div>
);

const OrderCard = ({ order, isReturn }) => (
  <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 12, background: '#fafafa' }}>
    <InfoGrid>
      <InfoItem
        label="Data"
        value={order.pickup_date ? moment(split(order.pickup_date, '::')[0]).format('DD/MM/YYYY') : '—'}
      />
      <InfoItem label="Hora" value={split(order.pickup_date || '', '::')[1] || '—'} />
      <InfoItem label="Estado" value={orderStatusLabel(order.status)} />
      <InfoItem
        label={isReturn ? 'Morada de entrega' : 'Morada de recolha'}
        value={order.location?.address || order.address?.full_address || order.address?.street || '—'}
      />
      {order.notes && <InfoItem label="Notas" value={order.notes} />}
    </InfoGrid>
  </div>
);

const ItemsTable = ({ items }) => (
  <TableContainer component={Paper} variant="outlined" style={{ marginTop: 8 }}>
    <Table size="small">
      <TableHead>
        <TableRow style={{ background: '#f5f5f5' }}>
          <TableCell><strong>ID</strong></TableCell>
          <TableCell><strong>Descrição</strong></TableCell>
          <TableCell align="center"><strong>Dimensão</strong></TableCell>
          <TableCell align="center"><strong>Estado</strong></TableCell>
          <TableCell align="center"><strong>Foto</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item, i) => {
          const status = getItemStatus(item);
          let subItems = item.sub_items;
          if (typeof subItems === 'string') {
            try { subItems = JSON.parse(subItems) || []; } catch { subItems = []; }
          }
          subItems = Array.isArray(subItems) ? subItems : [];
          const imgUrl = item.image_url || subItems[0]?.image_url;
          const sizeLabel = item.measured_size_m2
            ? `${item.measured_size_m2} m²`
            : item.original_size_m2
            ? `${item.original_size_m2} m²`
            : item.size
            ? `${item.size} m²`
            : '—';

          return (
            <TableRow key={item._id || i}>
              <TableCell style={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 120 }}>{item._id || (i + 1)}</TableCell>
              <TableCell>{item.content_name || item.description || item.name || '—'}</TableCell>
              <TableCell align="center">{sizeLabel}</TableCell>
              <TableCell align="center">
                <Chip label={status} size="small" style={{ ...itemStatusStyle(status), fontSize: 11 }} />
              </TableCell>
              <TableCell align="center">
                {imgUrl ? (
                  <a href={imgUrl} target="_blank" rel="noopener noreferrer">
                    <img src={imgUrl} alt="item" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                  </a>
                ) : '—'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─── Main component ──────────────────────────────────────────────────────────

const CustomerAppView = ({ open, onClose, order }) => {
  const [tab, setTab] = useState(0);
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const userId = order?.user_id?._id;
  const user = order?.user_id;

  useEffect(() => {
    if (!open) {
      setCustomerData(null);
      setFetchError(null);
      setTab(0);
      return;
    }
    if (!userId || customerData) return;
    setLoading(true);
    axios.get(`/admin/customer/${userId}`)
      .then(res => {
        setCustomerData(res.data.data);
        setLoading(false);
      })
      .catch(() => {
        setFetchError('Erro ao carregar dados do cliente.');
        setLoading(false);
      });
  }, [open, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const allItems = flatMap(order?.products || [], product => {
    if (product?.product_type_id?.type === 'box') return [];
    return product?.items || [];
  });

  const boxItems = flatMap(order?.products || [], product => {
    if (product?.product_type_id?.type !== 'box') return [];
    return product?.items || [];
  });

  const pickupOrders = customerData?.orders?.orders || [];
  const returnOrders = customerData?.returnOrders?.returnOrders || [];
  const subscriptions = customerData?.subscriptions || [];
  const invoices = customerData?.customer?.payment_history || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle
        disableTypography
        style={{
          background: KW_GREEN,
          color: '#fff',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: '1rem' }}>
          <span role="img" aria-label="olho">👁️</span>
          {' '}Ver como cliente — {user?.name || user?.email || ''}
        </span>
        <IconButton onClick={onClose} size="small" style={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <div style={{ borderBottom: '1px solid #e0e0e0' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="Agendamentos" />
          <Tab label="Subscrição" />
          <Tab label="Pagamentos" />
          <Tab label="Os Meus Itens" />
        </Tabs>
      </div>

      <DialogContent style={{ padding: 0, minHeight: 400 }}>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress style={{ color: KW_GREEN }} />
          </div>
        )}

        {fetchError && (
          <Box p={3}>
            <Typography color="error">{fetchError}</Typography>
          </Box>
        )}

        {!loading && !fetchError && (
          <>
            {/* ── TAB 0: AGENDAMENTOS ── */}
            <TabPanel value={tab} index={0}>
              <Section title="Recolhas">
                {pickupOrders.length > 0
                  ? pickupOrders.map(o => <OrderCard key={o._id} order={o} isReturn={false} />)
                  : <OrderCard order={order} isReturn={false} />
                }
              </Section>

              <Section title="Devoluções">
                {returnOrders.length > 0
                  ? returnOrders.map(o => <OrderCard key={o._id} order={o} isReturn={true} />)
                  : <Typography variant="body2" style={{ color: '#888' }}>Sem devoluções agendadas.</Typography>
                }
              </Section>
            </TabPanel>

            {/* ── TAB 1: SUBSCRIÇÃO ── */}
            <TabPanel value={tab} index={1}>
              <Section title="Detalhes da subscrição">
                <InfoGrid>
                  <InfoItem label="Plano de Pagamento" value={freqLabel(order?.payment_frequency)} />
                  <InfoItem label="Área em armazenamento" value={`${order?.current_m2 || order?.total_m2 || '—'} m²`} />
                  <InfoItem
                    label="Valor periódico (s/IVA)"
                    value={
                      order?.amount?.recurring?.value != null
                        ? `${round(Number(order.amount.recurring.value), 2)} ${order.amount.recurring.currency || '€'}`
                        : '—'
                    }
                  />
                  <InfoItem
                    label="Valor periódico (c/IVA)"
                    value={
                      order?.amount?.recurring?.value != null
                        ? `${round(Number(order.amount.recurring.value) * 1.23, 2)} €`
                        : '—'
                    }
                  />
                </InfoGrid>
              </Section>

              {subscriptions.length > 0 && (
                <Section title="Subscrições Stripe">
                  {subscriptions.map((sub, i) => (
                    <div
                      key={sub._id || sub.id || i}
                      style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 12, background: '#fafafa' }}
                    >
                      <InfoGrid>
                        <InfoItem label="ID Stripe" value={sub.stripe_subscription_id || sub.id} mono />
                        <InfoItem label="Estado" value={sub.status || '—'} />
                        <InfoItem
                          label="Início do período"
                          value={sub.current_period_start ? moment.unix(sub.current_period_start).format('DD/MM/YYYY') : '—'}
                        />
                        <InfoItem
                          label="Fim do período"
                          value={sub.current_period_end ? moment.unix(sub.current_period_end).format('DD/MM/YYYY') : '—'}
                        />
                      </InfoGrid>
                    </div>
                  ))}
                </Section>
              )}
            </TabPanel>

            {/* ── TAB 2: PAGAMENTOS ── */}
            <TabPanel value={tab} index={2}>
              {invoices.length === 0 ? (
                <Typography variant="body2" style={{ color: '#888' }}>Sem faturas registadas.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow style={{ background: '#f5f5f5' }}>
                        <TableCell><strong>ID Fatura</strong></TableCell>
                        <TableCell><strong>Tipo</strong></TableCell>
                        <TableCell><strong>Data</strong></TableCell>
                        <TableCell align="right"><strong>Valor</strong></TableCell>
                        <TableCell align="center"><strong>Estado</strong></TableCell>
                        <TableCell align="center"><strong>PDF</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map(inv => (
                        <TableRow key={inv._id}>
                          <TableCell style={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 160 }}>
                            {inv._id}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={invoiceTypeLabel(inv.invoice_type)}
                              size="small"
                              style={{ background: '#f3f4f6', color: '#374151', fontSize: 11 }}
                            />
                          </TableCell>
                          <TableCell>
                            {inv.stripe_created_at
                              ? moment.unix(inv.stripe_created_at).format('DD/MM/YYYY')
                              : inv.created_at
                              ? moment(inv.created_at).format('DD/MM/YYYY')
                              : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {inv.amount?.value != null
                              ? `${round(Number(inv.amount.value), 2)} ${inv.amount.currency || '€'}`
                              : '—'}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={inv.status === 'paid' ? 'Pago' : inv.status === 'open' ? 'Pendente' : (inv.status || '—')}
                              size="small"
                              style={
                                inv.status === 'paid'
                                  ? { background: KW_GREEN_LIGHT, color: KW_GREEN }
                                  : { background: '#fef3c7', color: '#92400e' }
                              }
                            />
                          </TableCell>
                          <TableCell align="center">
                            {inv.invoice_express_pdf_url ? (
                              <a href={inv.invoice_express_pdf_url} target="_blank" rel="noopener noreferrer" style={{ color: KW_GREEN, fontWeight: 600 }}>
                                PDF
                              </a>
                            ) : inv.hosted_invoice_url ? (
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" style={{ color: KW_GREEN }}>
                                Ver
                              </a>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

            {/* ── TAB 3: OS MEUS ITENS ── */}
            <TabPanel value={tab} index={3}>
              {allItems.length > 0 && (
                <Section title={`Itens em espaço (${allItems.length})`}>
                  <ItemsTable items={allItems} />
                </Section>
              )}
              {boxItems.length > 0 && (
                <Section title={`Itens em caixa (${boxItems.length})`}>
                  <ItemsTable items={boxItems} />
                </Section>
              )}
              {allItems.length === 0 && boxItems.length === 0 && (
                <Typography variant="body2" style={{ color: '#888' }}>
                  Sem itens armazenados nesta encomenda.
                </Typography>
              )}
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerAppView;

